import { forwardRef, useCallback, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ClipboardEvent } from 'react';

import { Combobox } from '@base-ui/react/combobox';
import { Field } from '@base-ui/react/field';
import { IconButton } from 'components/Button';
import { XIcon } from 'components/Icon';
import { FieldLabel } from 'components/InfoButton';
import { multiSelectOverflowRecipe } from 'components/MultiSelect';
import {
  selectItemLabelRecipe,
  selectItemRecipe,
  selectListRecipe,
  selectMenuRecipe,
  selectPositionerRecipe,
} from 'components/Select';
import { tagContentRecipe, tagLabelRecipe, tagRecipe, tagRemoveIconRecipe, tagRemoveRecipe } from 'components/Tag';
import CloseIcon from 'components/Tag/close.svg?react';
import {
  textInputBoxRecipe,
  textInputHelperRecipe,
  textInputLabelRecipe,
  textInputRequiredMarkRecipe,
  textInputRootRecipe,
  textInputTrailingButtonRecipe,
} from 'components/TextInput';
import { useInheritedDarkMode } from 'components/Theme';
import { cn, forwardBoxPress } from 'components/utils';

import {
  tagInputBoxRecipe,
  tagInputChipRecipe,
  tagInputControlRecipe,
  tagInputEmptyRecipe,
  tagInputLabelRecipe,
  tagInputOverflowRecipe,
  tagInputRowRecipe,
} from './TagInput.recipe';

import type { TagInputProps } from './types';
import type { FieldInfoProps } from 'components/InfoButton/types';

/**
 * The Create row's value. Prefixed with a character no typed value can start with, so it can never
 * collide with a real option or tag and is recognised in `onValueChange` before anything is stored.
 */
const CREATE_PREFIX = '\u0000create:';

const ROW_GAP = 4;

/**
 * How many tags fit on the resting line, with room for the remainder when some do not.
 *
 * "What fits", not a fixed count — the set's rule, since a 300px field fits one tag beside the counter
 * and a wider one fits more. Widths come from a hidden measuring row rather than from the live chips,
 * so hiding a chip can never change the measurement that decided to hide it. At least one tag always
 * shows; if even that does not fit, it truncates.
 *
 * A row with no width yet — not laid out, inside a hidden tab, or under jsdom — shows everything, which
 * is the safe reading; the observer re-measures once it has a size.
 */
const countTagsThatFit = (available: number, tagWidths: number[], counterWidth: number) => {
  const total = tagWidths.length;
  if (!(available > 0)) return total;

  for (let shown = total; shown >= 1; shown -= 1) {
    const tags = tagWidths.slice(0, shown).reduce((sum, width) => sum + width, 0) + ROW_GAP * (shown - 1);
    const counter = shown < total ? ROW_GAP + counterWidth : 0;

    if (tags + counter <= available) return shown;
  }

  return Math.min(total, 1);
};

/*
 * Design System 2.0 tag input — a field where a person builds a SET of values, each held as a
 * removable tag.
 *
 * Ported from the Figma `Tag Input` set (file 5aSkE0U4TeWbuIZDvccHjc, node 1472:151191), read
 * 2026-09-25. Props mirror the axes that are real inputs — `position`, `error`, `disabled`, and
 * `clearLabel` for the `rightButton` boolean — while `State`, `isFilled` and `isOpen` are observed
 * from the DOM, as on `TextInput`.
 *
 * ── When this is the wrong component ───────────────────────────────────────────────────────────
 *
 *  · picking from a fixed list, where chosen items are not removed one by one → `MultiSelect`
 *  · one value → `Select` or `TextInput`
 *
 * The dividing question is whether a person removes a member of the set INSIDE the field. Here they
 * do, by Backspace or the ✕; in `MultiSelect` they un-tick it in the list.
 *
 * ```tsx
 * <TagInput
 *   label="Community labels"
 *   options={[{ value: 'wellbeing', label: 'Wellbeing' }]}
 *   creatable
 *   labels={{
 *     overflow: (count) => t('tagInput.more', { count }),
 *     remove: (label) => t('tagInput.remove', { label }),
 *     instructions: t('tagInput.instructions'),
 *     added: (label) => t('tagInput.added', { label }),
 *     removed: (label) => t('tagInput.removed', { label }),
 *     create: (text) => t('tagInput.create', { text }),
 *   }}
 * />
 * ```
 *
 * ── Behaviour ──────────────────────────────────────────────────────────────────────────────────
 *
 * Base UI's `Combobox` in `multiple` mode, which supplies the accessibility contract the set asks
 * for: ONE tab stop, the input a combobox owning a listbox, the Create row an option like any other,
 * Backspace in an empty input removing the last tag, and ArrowLeft stepping onto the tags.
 *
 * At rest the field keeps one line — as many tags as fit, then "+N more" — so it holds its
 * neighbours' height in a form. While editing it wraps to show everything.
 *
 * Theme is not a prop. The portalled menu inherits the ambient colour scheme through
 * `useInheritedDarkMode`, as `Select` and `MultiSelect` do.
 *
 * The set's `Info` boolean is `info` + `infoLabel`, rendered by the shared `FieldLabel` so this field
 * places the button exactly as the other labelled fields do.
 */
/**
 * Build a set of typed or searched values, each shown as a removable Tag. Use MultiSelect instead
 * when people pick from a fixed list and deselect in it. Requires `label` and a `labels` object for
 * every string it draws or announces. `options` are optional suggestions; `creatable` adds a
 * Create row for typed text. Backspace removes the last tag and the ✕ stays out of the tab order.
 * At rest the field keeps one line and shows `+N more`; while editing it wraps. `State`, `isFilled`
 * and `isOpen` are browser states, not props, and FieldGroup supplies `position` when stacked.
 * `clearLabel` adds the trailing clear button. Pair `info` with `infoLabel` for optional context;
 * use `helperText` for instructions needed to fill the field.
 */
export const TagInput = forwardRef<HTMLInputElement, TagInputProps>(
  (
    {
      label,
      labels,
      options = [],
      creatable,
      value,
      defaultValue,
      onValueChange,
      inputValue,
      defaultInputValue,
      onInputValueChange,
      position,
      helperText,
      error,
      disabled,
      required,
      splitPastedLines,
      onPaste,
      onBlur,
      name,
      clearLabel,
      info,
      infoLabel,
      className,
      rootClassName,
      menuClassName,
      'data-testid': testId,
    },
    ref
  ) => {
    const hasHelper = Boolean(helperText);
    const hasClear = Boolean(clearLabel);
    const instructionsId = useId();
    // Re-paired after destructuring: `FieldLabel` takes the discriminated pair, not two optionals.
    const fieldInfo: FieldInfoProps = info && infoLabel ? { info, infoLabel } : {};

    const boxRef = useRef<HTMLDivElement>(null);
    const rowRef = useRef<HTMLDivElement>(null);
    const measureRef = useRef<HTMLDivElement>(null);

    /**
     * Both the tags and the typed text are mirrored here and handed to Base UI as CONTROLLED, whether
     * or not the caller controls them. The Create row has to be intercepted before a value is stored
     * (Base UI would otherwise store the sentinel), and the menu's rows are computed from the text, so
     * neither can be left to Base UI alone. A caller's controlled prop still out-ranks the mirror.
     */
    const [uncontrolledValue, setUncontrolledValue] = useState<string[]>(defaultValue ?? []);
    const tags = value ?? uncontrolledValue;

    const [uncontrolledInput, setUncontrolledInput] = useState(defaultInputValue ?? '');
    const text = inputValue ?? uncontrolledInput;
    const query = text.trim();

    const [requestedOpen, setRequestedOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [announcement, setAnnouncement] = useState('');

    const labelFor = useCallback(
      (tagValue: string) => options.find((option) => option.value === tagValue)?.label ?? tagValue,
      [options]
    );

    /**
     * The menu's rows: matching suggestions not already in the field, then the Create row.
     *
     * Chosen values are left out — a tag is removed in place, never by un-ticking a row, and the set
     * draws no selected row. The Create row is hidden when the text already names an option or a tag,
     * compared case-insensitively, so "Leadership" and "leadership" cannot both land.
     */
    const rows = useMemo(() => {
      const needle = query.toLowerCase();
      const matches = options
        .filter((option) => !tags.includes(option.value))
        .filter((option) => option.label.toLowerCase().includes(needle))
        .map((option) => option.value);

      const exists =
        options.some((option) => option.label.toLowerCase() === needle || option.value.toLowerCase() === needle) ||
        tags.some((tag) => labelFor(tag).toLowerCase() === needle);

      return creatable && query && !exists ? [...matches, CREATE_PREFIX + query] : matches;
    }, [creatable, labelFor, options, query, tags]);

    /**
     * Never an empty open panel. With nothing to offer, the menu shows the one "No results found" row
     * if the caller supplied it and the person has typed something; otherwise it stays shut.
     */
    const showEmpty = rows.length === 0 && Boolean(query) && Boolean(labels.noResults);
    const isOpen = requestedOpen && !disabled && (rows.length > 0 || showEmpty);

    const { sentinelRef, inheritedDarkClass } = useInheritedDarkMode(isOpen);

    const setText = useCallback(
      (next: string) => {
        setUncontrolledInput(next);
        onInputValueChange?.(next);
      },
      [onInputValueChange]
    );

    /**
     * Base UI's changes to the typed text, minus one it makes on its own behalf.
     *
     * A `multiple` Combobox clears the input once its popup has finished closing, however it closed.
     * This field closes the popup itself whenever it has no rows — and typing through the name of a
     * tag already in the field hides the Create row, so typing "test 2" beside a "test" tag closed
     * the empty menu at "test" and the clear swallowed the word mid-keystroke. A clear that is not an
     * item press, arriving while focus is still inside the field, is that cleanup, and is dropped:
     * the person is still typing. A pick, the clear button and leaving the field all still clear.
     */
    const handleInputValueChange = useCallback(
      (next: string, eventDetails: { reason: string; isItemPress?: boolean }) => {
        const isCleanupClear =
          next === '' &&
          eventDetails.reason === 'input-clear' &&
          !eventDetails.isItemPress &&
          Boolean(boxRef.current?.contains(document.activeElement));

        if (isCleanupClear) return;

        setText(next);
      },
      [setText]
    );

    const handleValueChange = useCallback(
      (next: string[]) => {
        const resolved = [
          ...new Set(next.map((item) => (item.startsWith(CREATE_PREFIX) ? item.slice(CREATE_PREFIX.length) : item))),
        ];

        const added = resolved.filter((item) => !tags.includes(item));
        const removed = tags.filter((item) => !resolved.includes(item));
        const message = [
          ...added.map((item) => labels.added(labelFor(item))),
          ...removed.map((item) => labels.removed(labelFor(item))),
        ];
        if (message.length > 0) setAnnouncement(message.join('. '));

        setUncontrolledValue(resolved);
        onValueChange?.(resolved);
        if (added.length > 0) setText('');
      },
      [labelFor, labels, onValueChange, setText, tags]
    );

    /**
     * A pasted list, one tag per line.
     *
     * Only with `creatable`: splitting makes new free-text tags, which a field restricted to its
     * options cannot hold. It goes through `handleValueChange`, so dedupe and the live-region
     * announcement are the same as for a typed tag. The caller's `onPaste` runs first and can opt out
     * with `preventDefault`, which is how it observes a paste without having to re-implement this.
     */
    const handlePaste = useCallback(
      (event: ClipboardEvent<HTMLInputElement>) => {
        onPaste?.(event);
        if (event.defaultPrevented || !splitPastedLines || !creatable) return;

        const pasted = event.clipboardData.getData('text');
        if (!/[\r\n]/.test(pasted)) return;

        const lines = pasted
          .split(/\r?\n/)
          .map((line) => line.trim())
          .filter(Boolean);

        event.preventDefault();
        if (lines.length > 0) handleValueChange([...tags, ...lines]);
      },
      [creatable, handleValueChange, onPaste, splitPastedLines, tags]
    );

    /**
     * How many tags the resting line draws. Re-measured whenever the tags, their labels or the field's
     * width change; while editing every tag shows, so there is nothing to measure.
     */
    const [fitCount, setFitCount] = useState(tags.length);

    useLayoutEffect(() => {
      const row = rowRef.current;
      const measure = measureRef.current;
      if (!row || !measure) return undefined;

      const update = () => {
        const style = getComputedStyle(row);
        const available = row.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
        const widths = [...measure.querySelectorAll<HTMLElement>('[data-measure=tag]')].map((node) => node.offsetWidth);
        const counter = measure.querySelector<HTMLElement>('[data-measure=counter]')?.offsetWidth ?? 0;

        setFitCount(countTagsThatFit(available, widths, counter));
      };

      update();
      const observer = new ResizeObserver(update);
      observer.observe(row);

      return () => observer.disconnect();
    }, [tags, labelFor, labels]);

    const shownTags = isEditing ? tags : tags.slice(0, fitCount);
    const hiddenCount = tags.length - shownTags.length;
    const hasTags = tags.length > 0;

    return (
      <Field.Root
        className={cn(textInputRootRecipe(), rootClassName)}
        data-editing={isEditing || undefined}
        data-has-tags={hasTags || undefined}
        data-testid={testId}
        disabled={disabled}
        invalid={error}
      >
        <Combobox.Root
          autoHighlight
          disabled={disabled}
          filter={null}
          inputValue={text}
          itemToStringLabel={(item: string) =>
            item.startsWith(CREATE_PREFIX) ? item.slice(CREATE_PREFIX.length) : labelFor(item)
          }
          items={rows}
          multiple
          name={name}
          open={isOpen}
          value={tags}
          onInputValueChange={handleInputValueChange}
          onOpenChange={setRequestedOpen}
          onValueChange={handleValueChange}
        >
          {/**
           * Focus-within, tracked here rather than read from `:focus-within`, because the same flag
           * decides how many tags render — see `tagInputRowRecipe`. A press on a chip's ✕ moves focus
           * to a node still inside the box, so it does not collapse the row under the pointer.
           */}
          <div
            ref={boxRef}
            className={cn(
              textInputBoxRecipe({ position, hasHelper }),
              tagInputBoxRecipe({ hasTags, hasHelper }),
              className
            )}
            data-part="box"
            onBlur={(event) => {
              if (event.currentTarget.contains(event.relatedTarget)) return;

              setIsEditing(false);
              onBlur?.();
            }}
            onFocus={() => setIsEditing(true)}
            onMouseDown={forwardBoxPress}
          >
            <Combobox.Chips
              ref={rowRef}
              className={tagInputRowRecipe({ hasTags, hasHelper, hasClear })}
              data-part="row"
            >
              {shownTags.map((tag) => (
                <Combobox.Chip
                  key={tag}
                  aria-label={labelFor(tag)}
                  className={cn(
                    tagRecipe({ size: 'sm', color: 'neutral', interactive: !disabled }),
                    tagInputChipRecipe()
                  )}
                  data-part="tag"
                >
                  <span className={tagContentRecipe()}>
                    <span className={tagLabelRecipe()}>{labelFor(tag)}</span>
                  </span>
                  {/* Out of the tab order by design — Backspace is the keyboard route. It is still the
                      Tag set's own `3xs` Tertiary button, so it keeps its hover and pressed steps. */}
                  <Combobox.ChipRemove
                    aria-label={labels.remove(labelFor(tag))}
                    render={
                      <IconButton
                        appearance="tertiary"
                        aria-label={labels.remove(labelFor(tag))}
                        className={tagRemoveRecipe()}
                        fill="default"
                        size="3xs"
                        tabIndex={-1}
                      >
                        <CloseIcon className={tagRemoveIconRecipe()} />
                      </IconButton>
                    }
                  />
                </Combobox.Chip>
              ))}

              {hiddenCount > 0 && (
                <span className={cn(multiSelectOverflowRecipe(), tagInputOverflowRecipe())} data-part="overflow">
                  {labels.overflow(hiddenCount)}
                </span>
              )}

              <Combobox.Input
                ref={ref}
                aria-describedby={instructionsId}
                aria-required={required || undefined}
                className={tagInputControlRecipe({ hasTags })}
                onPaste={handlePaste}
              />
            </Combobox.Chips>

            <FieldLabel
              {...fieldInfo}
              className={cn(textInputLabelRecipe(), tagInputLabelRecipe({ hasTags, hasHelper }))}
              disabled={disabled}
              label={label}
              required={required}
              requiredMarkClassName={textInputRequiredMarkRecipe()}
            />

            {clearLabel && (
              <Combobox.Clear
                render={
                  <IconButton
                    appearance="tertiary"
                    aria-label={clearLabel}
                    className={textInputTrailingButtonRecipe()}
                    data-part="clear"
                    fill="default"
                    size="2xs"
                    type="button"
                  >
                    <XIcon />
                  </IconButton>
                }
              />
            )}

            {hasHelper && error && (
              <Field.Error className={textInputHelperRecipe()} match>
                {helperText}
              </Field.Error>
            )}
            {hasHelper && !error && (
              <Field.Description className={textInputHelperRecipe()}>{helperText}</Field.Description>
            )}

            {/**
             * The measuring row: every tag and the remainder, drawn with the live classes but invisible
             * and out of the accessibility tree. The words come from `attr()` in a pseudo-element rather
             * than from text nodes, so the row takes the right width without putting a second copy of
             * every label into the DOM for `getByText` to find. The ✕ is a same-size placeholder.
             */}
            <div
              ref={measureRef}
              aria-hidden="true"
              className="pointer-events-none invisible absolute top-0 left-0 flex whitespace-nowrap"
            >
              {tags.map((tag) => (
                <span key={tag} className={tagRecipe({ size: 'sm', color: 'neutral' })} data-measure="tag">
                  <span className={tagContentRecipe()}>
                    <span
                      className={cn(tagLabelRecipe(), 'before:content-[attr(data-text)]')}
                      data-text={labelFor(tag)}
                    />
                  </span>
                  <span className={cn(tagRemoveRecipe(), 'inline-block size-[16px]')} />
                </span>
              ))}
              {tags.length > 1 && (
                <span
                  className={cn(multiSelectOverflowRecipe(), 'before:content-[attr(data-text)]')}
                  data-measure="counter"
                  data-text={labels.overflow(tags.length - 1)}
                />
              )}
            </div>
          </div>

          <span className="sr-only" id={instructionsId}>
            {labels.instructions}
          </span>
          <span aria-live="polite" className="sr-only" data-part="announcer">
            {announcement}
          </span>

          <span ref={sentinelRef} hidden />

          <Combobox.Portal className={inheritedDarkClass}>
            {/* The placement `Select` and `MultiSelect` use: anchored to the bordered box, 4px below it. */}
            <Combobox.Positioner
              align="start"
              anchor={boxRef}
              className={selectPositionerRecipe()}
              side="bottom"
              sideOffset={4}
            >
              <Combobox.Popup className={cn('ds-select', selectMenuRecipe(), menuClassName)} data-part="menu">
                {showEmpty && <div className={tagInputEmptyRecipe()}>{labels.noResults}</div>}
                <Combobox.List className={selectListRecipe()}>
                  {(item: string) => (
                    <Combobox.Item key={item} className={selectItemRecipe()} value={item}>
                      <span className={selectItemLabelRecipe()}>
                        {item.startsWith(CREATE_PREFIX)
                          ? (labels.create?.(item.slice(CREATE_PREFIX.length)) ?? item.slice(CREATE_PREFIX.length))
                          : labelFor(item)}
                      </span>
                    </Combobox.Item>
                  )}
                </Combobox.List>
              </Combobox.Popup>
            </Combobox.Positioner>
          </Combobox.Portal>
        </Combobox.Root>
      </Field.Root>
    );
  }
);
